import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner'; // Assuming you use sonner for notifications, like in your other hooks
import { expertBlogAPI } from '../../api/expertBlogApi'; // Adjust path

export function useExpertBlogs(expertCode: string | undefined) {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 🟢 NEW: Track which blog is currently being deleted for loading states
  const [isDeleting, setIsDeleting] = useState<number | string | null>(null);

  useEffect(() => {
    if (!expertCode) {
      setIsLoadingBlogs(false);
      return;
    }

    const fetchBlogs = async () => {
      setIsLoadingBlogs(true);
      setError(null);
      
      try {
        const data = await expertBlogAPI.getBlogsByExpert(expertCode);
        setBlogs(data || []);
      } catch (err) {
        console.error("Failed to fetch expert blogs:", err);
        setError("Could not load articles.");
      } finally {
        setIsLoadingBlogs(false);
      }
    };

    fetchBlogs();
  }, [expertCode]);

  // 🟢 NEW: Delete a blog and immediately remove it from the UI
  const deleteBlog = useCallback(async (blogId: string | number) => {
    setIsDeleting(blogId);
    try {
      await expertBlogAPI.deleteBlog(blogId);
      
      // Update local state instantly so the deleted card disappears without a page refresh
      setBlogs((prevBlogs) => prevBlogs.filter((blog) => blog.id !== blogId));
      
      toast.success("Article deleted successfully");
      return true;
    } catch (err) {
      console.error("Failed to delete blog:", err);
      toast.error("Failed to delete the article. Please try again.");
      return false;
    } finally {
      setIsDeleting(null);
    }
  }, []);

  return { 
    blogs, 
    setBlogs, // Exporting this so you can manually update the list after creating/editing a post
    isLoadingBlogs, 
    error,
    deleteBlog, // 🟢 Export the new delete function
    isDeleting  // 🟢 Export the deleting state (useful for disabling the delete button while loading)
  };
}